import { Bot, Check, Loader2, Send, Sparkles, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DocumentosLabels } from "@/lib/labels/documentos";
import type { SignatureAdjustment } from "@/types/signatures";

export function SendTermsAssistantConversation({
	termsSelected,
	history,
	isPending,
}: {
	termsSelected: boolean;
	history: { id: string; role: "user" | "assistant"; text: string }[];
	isPending: boolean;
}) {
	return (
		<div className="flex min-w-0 flex-col gap-3">
			<AssistantMessage>
				{termsSelected && DocumentosLabels.preparation.assistantReady}
				{!termsSelected && DocumentosLabels.preparation.assistantSelectTerms}
			</AssistantMessage>

			{history.map((entry) => {
				if (entry.role === "user") {
					return (
						<div
							key={entry.id}
							className="ml-9 min-w-0 break-words rounded-lg bg-primary px-3 py-2.5 text-sm leading-5 text-primary-foreground"
						>
							{entry.text}
						</div>
					);
				}

				return <AssistantMessage key={entry.id}>{entry.text}</AssistantMessage>;
			})}

			{isPending && (
				<div className="ml-9 flex items-center gap-2 text-sm text-muted-foreground">
					<Loader2 className="size-4 animate-spin" />
					{DocumentosLabels.preparation.assistantWorking}
				</div>
			)}
		</div>
	);
}

function AssistantMessage({ children }: { children: React.ReactNode }) {
	return (
		<div className="flex items-start gap-2">
			<div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
				<Bot className="size-3.5" />
			</div>
			<p className="surface-panel min-w-0 break-words px-3 py-2.5 text-sm leading-5 text-foreground">
				{children}
			</p>
		</div>
	);
}

export function SendTermsAssistantProposal({
	proposal,
	onDiscard,
	onApply,
}: {
	proposal: SignatureAdjustment;
	onDiscard: () => void;
	onApply: () => void;
}) {
	return (
		<div className="surface-panel ml-9 border-warning/40 p-3">
			<div className="flex flex-col gap-3">
				{proposal.changes.map((change) => (
					<div key={`${change.termId}_${change.fieldId}_${change.itemId ?? "plain"}`}>
						<p className="text-xs font-semibold text-warning">{change.label}</p>
						<div className="mt-2 grid grid-cols-2 gap-2">
							<div className="rounded-md bg-background p-2">
								<p className="text-xs text-muted-foreground">Atual</p>
								<p className="mt-1 text-sm text-foreground line-through opacity-60">
									{change.before || DocumentosLabels.preparation.missingValue}
								</p>
							</div>
							<div className="rounded-md bg-whatsapp/10 p-2">
								<p className="text-xs text-whatsapp">Proposta</p>
								<p className="mt-1 text-sm font-medium text-foreground">{change.after}</p>
							</div>
						</div>
						<p className="mt-2 text-xs text-muted-foreground">{change.explanation}</p>
					</div>
				))}
			</div>
			<div className="mt-3 flex justify-end gap-2">
				<Button type="button" variant="secondary" size="sm" onClick={onDiscard}>
					<X />
					Descartar
				</Button>
				<Button type="button" variant="default" size="sm" onClick={onApply}>
					<Check />
					Aplicar ajustes
				</Button>
			</div>
		</div>
	);
}

export function SendTermsAssistantComposer({
	message,
	disabled,
	onMessageChange,
	onSubmit,
}: {
	message: string;
	disabled: boolean;
	onMessageChange: (message: string) => void;
	onSubmit: () => void;
}) {
	return (
		<div className="rounded-xl border border-border bg-card p-2 shadow-card">
			<div className="flex items-end gap-2">
				<div className="flex min-w-0 flex-1 items-start gap-2 px-2 py-1.5">
					<Sparkles className="mt-0.5 size-4 shrink-0 text-primary" />
					<textarea
						data-slot="composer"
						aria-label={DocumentosLabels.preparation.askAssistant}
						value={message}
						onChange={(event) => onMessageChange(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter" && !event.shiftKey) {
								event.preventDefault();
								onSubmit();
							}
						}}
						rows={2}
						maxLength={500}
						placeholder={DocumentosLabels.preparation.assistantPlaceholder}
						disabled={disabled}
						className="max-h-28 min-h-6 w-full resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:opacity-50"
					/>
				</div>
				<button
					data-slot="submit"
					type="button"
					onClick={onSubmit}
					disabled={disabled || message.trim() === ""}
					aria-label={DocumentosLabels.preparation.askAssistant}
					className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-30"
				>
					<Send className="size-4" />
				</button>
			</div>
		</div>
	);
}
